// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { useEffect, useRef, useState } from "react";

import {
  createAskV2Transport,
  type AskV2Transport,
} from "./createAskV2Transport";
import { type AskV2TransportSnapshot } from "./types";

import { generateUUID } from "../../utils/uuid";
import {
  abortAskV2Message,
  createAskV2Chat,
  regenerateAskV2Message,
  sendAskV2Message,
} from "../services/chat";

export type AskV2TransportApi = AskV2TransportSnapshot & {
  setComposer: AskV2Transport["setComposer"];
  send: AskV2Transport["send"];
  abort: AskV2Transport["abort"];
  retry: AskV2Transport["retry"];
  beginEdit: AskV2Transport["beginEdit"];
  setEditDraft: AskV2Transport["setEditDraft"];
  cancelEdit: AskV2Transport["cancelEdit"];
  submitEdit: AskV2Transport["submitEdit"];
};

export function useAskV2Transport(): AskV2TransportApi {
  const transportRef = useRef<AskV2Transport | null>(null);
  if (transportRef.current == null) {
    transportRef.current = createAskV2Transport({
      id: generateUUID,
      api: {
        createChat: ({ message }) => createAskV2Chat(message),
        sendMessage: ({ chatId, message }) => sendAskV2Message(chatId, message),
        abortMessage: ({ chatId, messageId }) =>
          abortAskV2Message(chatId, messageId),
        regenerateMessage: ({ chatId, messageId, content }) =>
          regenerateAskV2Message(chatId, messageId, content),
      },
    });
  }

  const transport = transportRef.current;
  const [snapshot, setSnapshot] = useState(transport.getSnapshot);

  useEffect(() => transport.subscribe(setSnapshot), [transport]);

  return {
    ...snapshot,
    setComposer: transport.setComposer,
    send: transport.send,
    abort: transport.abort,
    retry: transport.retry,
    beginEdit: transport.beginEdit,
    setEditDraft: transport.setEditDraft,
    cancelEdit: transport.cancelEdit,
    submitEdit: transport.submitEdit,
  };
}
